<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import Konva from "konva";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import useLasso from "@/components/mobile/generation/inpaint/useLasso";
import { Overlay, showImagePreview, showLoadingToast } from "vant";
import useRecSelect from "@/components/mobile/generation/inpaint/useRecSelect";
import useBrush from "@/components/mobile/generation/inpaint/useBrush";
import UndoIcon from "@/components/icon/UndoIcon.vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import Shape = Konva.Shape;
import i18n from "@/locales";
import useZoom from "../custom-painting/useZoom";
import { resetReactPosition, undoReactPosition } from "./common";

// Props
const emit = defineEmits<{
  (e: "onFinished", params: { base64: string }): void;
}>();

// State
const show = ref(false);
const mediaUrl = ref("");
const main = ref<HTMLDivElement | null>(null);
const stage = ref<Konva.Stage | null>(null);
const selectionLayer = ref<Konva.Layer | null>(null);
const selectionMode = ref<"lasso" | "rect" | "brush" | "eraser" | "move">(
  "lasso"
);
const isMove = computed(() => selectionMode.value === "move");
const isLasso = computed(() => selectionMode.value === "lasso");
const isRect = computed(() => selectionMode.value === "rect");
const isBrush = computed(() => selectionMode.value === "brush");
const isEraser = computed(() => selectionMode.value === "eraser");
const history = ref<Konva.Shape[]>([]);
const brushWidth = ref(14);
const eraseWidth = ref(14);
let image: HTMLImageElement | null = null;

const zoomPercent = computed(() => Math.floor(zoomRate.value * 100));

// Lifecycle
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

let canvasWidth = 0;
let canvasHeight = 0;
const calcCanvasSize = (
  imgWidth: number,
  imgHeight: number,
  maxWidth: number,
  maxHeight: number
) => {
  const imgRatio = imgWidth / imgHeight;
  const containerRatio = maxWidth / maxHeight;
  if (imgRatio > containerRatio) {
    canvasWidth = maxWidth;
    canvasHeight = maxWidth / imgRatio;
  } else {
    canvasHeight = maxHeight;
    canvasWidth = maxHeight * imgRatio;
  }
  return { canvasWidth, canvasHeight };
};
const prepareKonva = async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  try {
    // 1. 加载图片，根据图片和容器确定画布的宽高
    // 获取main的宽高
    const { width, height } = main.value!.getBoundingClientRect();
    image = await loadImage(mediaUrl.value);
    const { canvasWidth, canvasHeight } = calcCanvasSize(
      image.width,
      image.height,
      width,
      height
    );
    // 2. 初始化Konva
    stage.value = new Konva.Stage({
      container: "container",
      width: canvasWidth,
      height: canvasHeight,
    });
    // 3. 添加图片图层：加载图片并添加到layer中
    const imgLayer = new Konva.Layer();
    const imgNode = new Konva.Image({
      image: image,
      width: canvasWidth,
      height: canvasHeight,
    });
    imgLayer.add(imgNode);
    stage.value?.add(imgLayer);
    // 4. 不透明度20%的黑色蒙板
    const mask = new Konva.Rect({
      width: canvasWidth,
      height: canvasHeight,
      fill: "#000",
      opacity: 0.4,
    });
    imgLayer.add(mask);
    // 5. 添加选区图层
    const layer = new Konva.Layer();
    stage.value?.add(layer);
    selectionLayer.value = layer;
  } finally {
    loading.close();
  }
};
watch(
  show,
  () => {
    if (!show.value) {
      stage.value?.destroy();
      history.value = [];
      selectionMode.value = "lasso";
      return;
    }
    nextTick(() => {
      prepareKonva();
    });
  },
  { flush: "post" }
);
onBeforeUnmount(() => {
  stage.value?.destroy();
});
// Event Handler
const onShapeCreate = (s: Shape) => history.value.push(s);
const { resetZoom, zoomRate, offset } = useZoom(
  stage,
  selectionLayer,
  isMove,
  true
);
useLasso(stage, selectionLayer, isLasso, onShapeCreate);
useRecSelect(stage, selectionLayer, isRect, onShapeCreate);
useBrush(stage, selectionLayer, isBrush, onShapeCreate, {
  strokeWidth: brushWidth,
});
useBrush(stage, selectionLayer, isEraser, onShapeCreate, {
  strokeWidth: eraseWidth,
  mode: "eraser",
});
const undo = () => {
  const last = history.value.pop();
  undoReactPosition();
  if (last) {
    last.destroy();
  }
};
const clear = () => {
  history.value.forEach((s) => s.destroy());
  resetReactPosition();
  history.value = [];
};
const onConfirm = () => {
  let maskWidth = 768;
  let maskHeight = 1024;
  if (image!.width! > image!.height!) {
    maskWidth = 1280;
    maskHeight = 720;
  }
  const exportStage = new Konva.Stage({
    container: "export-container",
    width: maskWidth,
    height: maskHeight,
  });
  // 1. 设置用于导出的图层
  const exportLayer = new Konva.Layer();
  exportStage.add(exportLayer);
  // 2. 设置背景
  const background = new Konva.Rect({
    width: exportStage.width(),
    height: exportStage.height(),
    fill: "rgb(26, 26, 26)", // 必须是这个颜色
  });
  exportLayer.add(background);
  // 3. 复制：选区图层 => 导出图层，并等比例缩放
  const scaleX = exportStage.width() / stage.value!.width();
  const scaleY = exportStage.height() / stage.value!.height();
  selectionLayer.value?.children.forEach((c) => {
    const shape = c.clone({
      x: c.x() * scaleX,
      y: c.y() * scaleY,
      stroke: "#fff",
      fill: "#fff",
      opacity: undefined,
      scaleX,
      scaleY,
    }) as Shape;
    if (shape.globalCompositeOperation() === "destination-out") {
      // 橡皮擦使用destination-out，需要将颜色设置为黑色
      shape.fill("rgb(26, 26, 26)");
      shape.stroke("rgb(26, 26, 26)");
      shape.globalCompositeOperation("source-over");
    }
    exportLayer.add(shape);
  });
  const base64 = exportLayer.toDataURL({ mimeType: "image/png" });
  emit("onFinished", { base64 });
  show.value = false;
  exportStage.destroy();
};

const start = (url: string) => {
  show.value = true;
  mediaUrl.value = url;
  resetReactPosition();
};

export interface InpaintingEditorInstance {
  start: (url: string) => void;
}

defineExpose({
  start,
});
</script>
<template>
  <overlay :show="show" class="grid grid-cols-[1fr] bg-base-100">
    <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
      <threads-nav :show-center="false">
        <template #left>
          <button class="btn btn-ghost" @click="show = false">
            <light-arrow-icon />
          </button>
        </template>
        <template #center>
          <button class="btn btn-xs btn-neutral" @click="resetZoom">
            {{ $t("Texts.zoom") }}: {{ zoomPercent }}%,
            {{ $t("Texts.coordinate") }}: ({{ Math.floor(offset.x) }},
            {{ Math.floor(offset.y) }})
          </button>
        </template>
        <template #right>
          <button class="btn btn-sm btn-primary" @click="onConfirm">
            {{ $t("Buttons.confirm") }}
          </button>
        </template>
      </threads-nav>
      <div ref="main" class="overflow-hidden flex items-center justify-center">
        <div id="container" crossorigin="anonymous"></div>
        <div id="export-container" class="hidden"></div>
      </div>
      <div class="flex gap-2 bg-base-200 p-2 w-full justify-between">
        <div class="flex gap-2">
          <button
            :class="{ 'btn-primary': isLasso, 'btn-neutral': !isLasso }"
            class="btn btn-xs"
            @click="selectionMode = 'lasso'"
          >
            {{ $t("Buttons.lasso") }}
          </button>
          <button
            :class="{ 'btn-primary': isRect, 'btn-neutral': !isRect }"
            class="btn btn-xs"
            @click="selectionMode = 'rect'"
          >
            {{ $t("Buttons.frameSelection") }}
          </button>
          <button
            :class="{ 'btn-primary': isBrush, 'btn-neutral': !isBrush }"
            class="btn btn-xs"
            @click="selectionMode = 'brush'"
          >
            {{ $t("Buttons.smear") }}
          </button>
          <button
            :class="{ 'btn-primary': isMove, 'btn-neutral': !isMove }"
            class="btn btn-xs"
            @click="selectionMode = 'move'"
          >
            <i class="iconfont icon-hand" />
          </button>
        </div>
        <div class="flex gap-2">
          <button
            :class="{ 'btn-primary': isEraser, 'btn-neutral': !isEraser }"
            class="btn btn-xs"
            @click="selectionMode = 'eraser'"
          >
            <i class="iconfont icon-rubber-full"></i>
          </button>
          <button
            :disabled="history.length === 0"
            class="btn btn-xs btn-neutral"
            @click="undo"
          >
            <undo-icon class="icon-sm" />
          </button>
          <button
            :disabled="history.length === 0"
            class="btn btn-xs btn-neutral"
            @click="clear"
          >
            {{ $t("Buttons.clear") }}
          </button>
        </div>
      </div>
    </div>
  </overlay>
</template>
